import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import * as Utils from "./utils";
import { normalizePermessi, hasPrivilegeFromList } from "./utils/permissions";
import { APP_VERSION } from "./utils/version";
import TopBar from "./TopBar";
import Login from "./sezioni/Login";
import RichiesteStorico from "./sezioni/RichiesteStorico";
import InfoUtente from "./sezioni/InfoUtente";
import Timbratore from "./sezioni/Timbratore";
import Turni from "./sezioni/Turni";
import MessaggiStorico from "./sezioni/MessaggiStorico";
import DevTools from "./sezioni/Sviluppo";
import LogoUssBlu from "./assets/LogoUssBlu.png";
import EasyJobLogo from "./assets/EasyJob_logo.png";
import { API_BASE_URL } from "./config";
import { api, ApiError } from "./api/client";
import { clearBiometricVault } from "./utils/biometric";
import { useMessage } from "./components/MessageProvider";
import {
  getMissingPushApi,
  markTraced,
  PUSH_MESSAGES,
  tracePushError,
  tracePushConfirm,
  tracePushRefusal,
  tracePushUnexpected,
} from "./utils/pushTrace";
import {
  automaticRegistration,
  markPushRegisteredHere,
  silentRenewal,
  unsubscribeAfterPermission,
  wasPushRegisteredHere,
} from "./utils/pushPolicy";

const VAPID_PUBLIC_KEY = "BLixF7HXcwT-tmCW3PfSL7MJ5YrBkZg3ymwtgWX6gXA8BxvBdXLsUFZAwaQZGdINtcBO_bSGYu1VqFJJlTWPls8";

// REGISTRAZIONE SERVICE WORKER - deve essere fuori dal componente App
// Skip in Capacitor: l'APK nativa ha già persistenza propria, il SW della PWA
// intercetterebbe le fetch e servirebbe cache obsoleta dal WebView.
const isCapacitor = typeof window !== "undefined" && !!window.Capacitor;
if ('serviceWorker' in navigator && !isCapacitor) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(reg => {
        console.log('Service Worker registrato:', reg);
      })
      .catch(err => {
        console.error('Errore registrazione Service Worker:', err);
      });
  });
  // Reload automatico quando un nuovo SW prende il controllo — evita che un tab
  // già aperto resti bloccato su una versione vecchia (JS/IndexedDB disallineati).
  // Non deve scattare alla primissima attivazione (nessun controller precedente,
  // prima visita in assoluto) — solo su un vero cambio di versione a runtime.
  let hadController = !!navigator.serviceWorker.controller;
  let swReloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    if (swReloading) return;
    swReloading = true;
    window.location.reload();
  });
}
else if (isCapacitor) {
  // In caso di build precedenti che avessero registrato un SW, lo rimuoviamo.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations?.().then(regs => {
      regs.forEach(r => r.unregister());
    }).catch(() => {});
  }
  console.log('Capacitor rilevato: Service Worker disabilitato.');
  // Disabilita auto-scroll iOS al focus input — evita che la tastiera "spinga via"
  // la TopBar e gli elementi fixed. No-op su Android (API iOS-only del plugin).
  import('@capacitor/keyboard').then(({ Keyboard }) => {
    Keyboard.setScroll({ isDisabled: true }).catch(() => {});
  }).catch(() => {});
}
else
{
  console.log('HTTPS non supportato. Service-worker non avviato.');
}

const drawerIcons = {
  timbratore: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l3 3" />
    </svg>
  ),
  turni: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="4.5" width="17" height="16" rx="2" />
      <path d="M8 2.5v4" />
      <path d="M16 2.5v4" />
      <path d="M3.5 9.5h17" />
      <path d="M9 13h6" />
      <path d="M9 16h3" />
    </svg>
  ),
  storico: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4.5" y="3.5" width="15" height="17" rx="2.5" />
      <path d="M8.5 8h7" />
      <path d="M8.5 12h7" />
      <path d="M8.5 16h7" />
    </svg>
  ),
  infoUtente: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="9" r="3" />
      <path d="M6 19c1.5-3 10.5-3 12 0" />
    </svg>
  ),
  sviluppo: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18V6l-3 3" />
      <path d="M15 6v12l3-3" />
    </svg>
  ),
  logout: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 3.5H7.8a2.3 2.3 0 0 0-2.3 2.3v12.4a2.3 2.3 0 0 0 2.3 2.3H10" />
      <path d="M16 7l4 5-4 5" />
      <path d="M20 12H10" />
    </svg>
  ),
  messaggi: (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <path d="M8 9h9" />
      <path d="M8 13h6" />
    </svg>
  ),
};

const pickSectionByPriority = (permessiList) => {
  const list = Array.isArray(permessiList) ? permessiList : [];
  if (list.includes("EJTI")) return "timbratore";
  if (list.includes("EJRI")) return "richieste";
  if (list.includes("EJTU")) return "turni";
  return "infoUtente";
};

const SECTION_PATHS = {
  timbratore: "/timbratore",
  turni: "/turni",
  richieste: "/richieste",
  infoUtente: "/info-utente",
  sviluppo: "/sviluppo",
  messaggi: "/messaggi",
};

const getSectionFromPathname = (pathname = "/") => {
  const normalized = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  if (normalized === "/turni") return "turni";
  if (normalized === "/timbratore") return "timbratore";
  if (normalized === "/richieste" || normalized === "/storico" || normalized === "/nuova-richiesta") return "richieste";
  if (normalized === "/info-utente") return "infoUtente";
  if (normalized === "/sviluppo") return "sviluppo";
  if (normalized === "/messaggi" || normalized.startsWith("/messaggi/")) return "messaggi";
  return null;
};

const hasAccessToSection = (sectionName, permessiList) => {
  const list = Array.isArray(permessiList) ? permessiList : [];
  if (sectionName === "timbratore") return list.includes("EJTI");
  if (sectionName === "turni") return list.includes("EJTU");
  if (sectionName === "richieste") return list.includes("EJRI");
  if (sectionName === "sviluppo") return list.includes("EJ-Developper") || list.includes("EJDEV");
  if (sectionName === "messaggi") return list.includes("EJME");
  return true;
};

const pickSectionFromUrlOrPriority = (permessiList) => {
  if (typeof window !== "undefined") {
    const routeSection = getSectionFromPathname(window.location.pathname);
    if (routeSection && hasAccessToSection(routeSection, permessiList)) {
      return routeSection;
    }
  }
  return pickSectionByPriority(permessiList);
};

// Legge e valida l'utente salvato in localStorage. Se il valore è corrotto
// (scrittura interrotta, bug pregresso, tampering) ripulisce la chiave invece
// di lanciare un'eccezione nel render iniziale — un JSON.parse non protetto qui
// bloccherebbe l'intera app in schermata bianca senza recovery possibile.
function getStoredUserSafe() {
  const storedUser = localStorage.getItem("easyjob_user");
  if (!storedUser) return null;
  try {
    return JSON.parse(storedUser);
  } catch (err) {
    console.warn("easyjob_user corrotto in localStorage, ripulito:", err);
    localStorage.removeItem("easyjob_user");
    return null;
  }
}

function App() {
  const [section, setSection] = useState(() => {
    const parsedUser = getStoredUserSafe();
    const initialPermessi = normalizePermessi(parsedUser?.permessi);
    return pickSectionFromUrlOrPriority(initialPermessi);
  });
  // Stato per mostrare la modale QR
  const [showQrReader, setShowQrReader] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [topBarAction, setTopBarAction] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem("easyjob_theme") || "light");
  const [isOnline, setIsOnline] = useState(Utils.isOnline());

  // All'avvio
  const [user, setUser] = useState(() => {
    if (!localStorage.getItem("easyjob_user")) return null;
    // Banking-style: dentro APK con vault biometrico, ogni cold start richiede sblocco.
    const isCapacitorApp = typeof window !== "undefined" && !!window.Capacitor;
    const biometricEnrolled = localStorage.getItem("easyjob_biometric_enrolled") === "1";
    if (isCapacitorApp && biometricEnrolled) return null;
    return getStoredUserSafe();
  });
  const [pushStatusReady, setPushStatusReady] = useState(false);
  const [pushCheckTrigger, setPushCheckTrigger] = useState(0);
  const persistUserData = useCallback((nextUser) => {
    if (!nextUser) return;
    try {
      localStorage.setItem("easyjob_user", JSON.stringify(nextUser));
    } catch (err) {
      console.warn("Impossibile salvare l'utente su localStorage", err);
    }
    Utils.saveUserOffline(nextUser).catch((err) => {
      console.warn("Impossibile salvare l'utente offline", err);
    });
  }, []);
  const lastUserIdRef = useRef(null);

  const { showMessage, showQuestion } = useMessage();
  const pushPromptSkipRef = useRef(false);

  const appName = process.env.REACT_APP_EASYJOB_ENV === 'dev' ? 'EasyJob (DEV)' : 'EasyJob';
  const titles = {
    richieste: "Richieste",
    infoUtente: "Profilo",
    timbratore: "Timbratore",
    turni: "Turni",
    sviluppo: "Sviluppo",
    messaggi: "Messaggi",
  };

  const sectionIcons = {
    timbratore: drawerIcons.timbratore,
    turni: drawerIcons.turni,
    richieste: drawerIcons.storico,
    infoUtente: drawerIcons.infoUtente,
    messaggi: drawerIcons.messaggi,
    sviluppo: drawerIcons.sviluppo,
  };

  const normalizedPermessi = useMemo(() => normalizePermessi(user?.permessi), [user?.permessi]);
  const drawerButtonClass = "p-4 text-left uppercase tracking-wide text-sm font-semibold hover:bg-gray-50 transition-colors";
  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === "light" ? "dark" : "light"));
  }, []);

  useEffect(() => {
    const nextClass = theme === "dark" ? "theme-dark" : "theme-light";
    document.body.classList.remove("theme-dark", "theme-light");
    document.body.classList.add(nextClass);
    localStorage.setItem("easyjob_theme", theme);
  }, [theme]);

  useEffect(() => {
    console.debug("Permessi normalizzati:", normalizedPermessi);
  }, [normalizedPermessi]);

  useEffect(() => {
    if (!user) return;
    const identifier = user.id ?? user.username ?? null;
    if (!identifier) return;
    if (lastUserIdRef.current === identifier) return;

    lastUserIdRef.current = identifier;
    const defaultSection = pickSectionFromUrlOrPriority(normalizedPermessi);
    setSection((prev) => (prev === defaultSection ? prev : defaultSection));
    console.debug("Sezione iniziale impostata per l'utente attivo:", defaultSection);
  }, [user, normalizedPermessi]);

  useEffect(() => {
    if (!user || typeof window === "undefined") return undefined;
    const handlePopState = () => {
      const routeSection = getSectionFromPathname(window.location.pathname);
      if (!routeSection) return;
      if (!hasAccessToSection(routeSection, normalizedPermessi)) return;
      setSection((prev) => (prev === routeSection ? prev : routeSection));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [user, normalizedPermessi]);

  useEffect(() => {
    if (!user || typeof window === "undefined") return;
    const targetPath = SECTION_PATHS[section] || "/";
    const targetSearch = section === "turni" ? window.location.search : "";
    const current = `${window.location.pathname}${window.location.search}`;
    const next = `${targetPath}${targetSearch}`;
    if (current !== next) {
      window.history.replaceState(null, "", next);
    }
  }, [section, user]);

  const hasPrivilege = useCallback(
    (code) => hasPrivilegeFromList(normalizedPermessi, code),
    [normalizedPermessi]
  );

  const canSeeRichieste = hasPrivilege("EJRI");
  const canSeeTimbratore = hasPrivilege("EJTI");
  const canSeeTurni = hasPrivilege("EJTU");
  const canUseDevTools = hasPrivilege("EJ-Developper") || hasPrivilege("EJDEV");
  const canSeeMessaggi = hasPrivilege("EJME");

  useEffect(() => {
    console.debug(`Menu richieste ${canSeeRichieste ? "VISIBILE" : "NASCOSTO"}`);
  }, [canSeeRichieste]);

  useEffect(() => {
    console.debug(`Menu timbratore ${canSeeTimbratore ? "VISIBILE" : "NASCOSTO"}`);
  }, [canSeeTimbratore]);

  useEffect(() => {
    console.debug(`Menu turni ${canSeeTurni ? "VISIBILE" : "NASCOSTO"}`);
  }, [canSeeTurni]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const syncAllData = useCallback(async () => {
    try {
      await Utils.syncAll({ user });
    } catch (err) {
      console.error("Errore sincronizzazione dati offline", err);
    }
  }, [user]);

  useEffect(() => {
    if (isOnline) {
      syncAllData();
    }
  }, [isOnline, syncAllData]);
  useEffect(() => {
    if (!user) return;
    syncAllData();
    const lastFocusSyncRef = { current: 0 };
    const handleFocusSync = () => {
      if (!navigator.onLine) return;
      if (Date.now() - lastFocusSyncRef.current < 60000) return;
      lastFocusSyncRef.current = Date.now();
      syncAllData();
    };
    window.addEventListener("focus", handleFocusSync);
    return () => {
      window.removeEventListener("focus", handleFocusSync);
    };
  }, [user, syncAllData]);

  useEffect(() => {
    if (!localStorage.getItem("easyjob_user") && user) {
      setUser(null);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (section === "richieste" && !canSeeRichieste) {
      if (canSeeTimbratore) {
        setSection("timbratore");
      } else {
        setSection("infoUtente");
      }
      return;
    }
    if (section === "timbratore" && !canSeeTimbratore) {
      if (canSeeRichieste) {
        setSection("richieste");
      } else {
        setSection("infoUtente");
      }
    }
    if (section === "turni" && !canSeeTurni) {
      if (canSeeTimbratore) {
        setSection("timbratore");
      } else if (canSeeRichieste) {
        setSection("richieste");
      } else {
        setSection("infoUtente");
      }
    }
    if (section === "sviluppo" && !canUseDevTools) {
      if (canSeeTimbratore) {
        setSection("timbratore");
      } else if (canSeeRichieste) {
        setSection("richieste");
      } else {
        setSection("infoUtente");
      }
    }
    if (section === "messaggi" && !canSeeMessaggi) {
      setSection(pickSectionByPriority(normalizedPermessi));
    }
  }, [user, section, canSeeRichieste, canSeeTimbratore, canSeeTurni, canUseDevTools, canSeeMessaggi, normalizedPermessi]);

  const handleLogout = () => {
    localStorage.removeItem("easyjob_user");
    clearBiometricVault().catch(() => {});
    lastUserIdRef.current = null;
    setUser(null);
    setSection("richieste");
  };

  const handleLogin = async (user) => {
    const permessiLogin = normalizePermessi(user?.permessi);
    console.debug("Permessi dal login:", permessiLogin);
    setUser(user);
    persistUserData(user);
    const defaultSection = pickSectionFromUrlOrPriority(permessiLogin);
    setSection(defaultSection);
    setMenuOpen(false);
    console.debug("Sezione iniziale post login:", defaultSection);
    showMessage({ title: "Accesso effettuato", message: `Benvenuto/a ${user.username}`, type: "info", timeout: 3 });
  };

  const ensurePushSubscription = useCallback(
    async ({ force = false, targetUser, askConfirmation = false } = {}) => {
      const activeUser = targetUser ?? user;
      if (!activeUser) {
        throw new Error("Nessun utente attivo");
      }

      const modalita = force ? "manuale" : "automatica";

      if (
        !("serviceWorker" in navigator) ||
        !("PushManager" in window) ||
        !("Notification" in window)
      ) {
        // Il messaggio per l'utente è lo stesso scritto nel registro (`esito`).
        throw markTraced(new Error(tracePushError(activeUser, getMissingPushApi(), { passo: "support_check", modalita })));
      }

      // Solo traccia: se il service worker non è ancora pronto dopo 10 secondi lo scrive nel registro,
      // senza interrompere l'attesa (su un telefono lento potrebbe comunque arrivare).
      const swReadyTimer = setTimeout(() => {
        tracePushError(activeUser, "sw_not_ready", { passo: "sw_ready", modalita });
      }, 10000);
      let registration;
      try {
        registration = await navigator.serviceWorker.ready;
      } finally {
        clearTimeout(swReadyTimer);
      }

      // Chiede il permesso se non è già concesso; se non arriva, lo scrive nel registro e si ferma.
      const requirePermission = async () => {
        if (Notification.permission === "granted") return;
        const permessoIniziale = Notification.permission;
        const requestStartedAt = Date.now();
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          const message = tracePushError(activeUser, "permission_denied", {
            passo: "permission",
            modalita,
            permessoIniziale,
            permessoRisposta: permission,
            durataMs: Date.now() - requestStartedAt,
          });
          throw markTraced(new Error(message));
        }
      };

      if (force) {
        // Prima il permesso, poi la cancellazione: se il permesso è bloccato o negato,
        // l'iscrizione esistente di questo dispositivo resta com'è.
        await unsubscribeAfterPermission(registration, requirePermission);
      }

      const savedTokens = activeUser.token_notifiche
        ? activeUser.token_notifiche.split(";").filter(Boolean)
        : [];
      const backendHasThisDevice =
        typeof activeUser.token_notifiche_reg === "boolean"
          ? activeUser.token_notifiche_reg
          : savedTokens.length > 0;
      const backendHasAnyToken =
        typeof activeUser.token_notifiche_has_any === "boolean"
          ? activeUser.token_notifiche_has_any
          : savedTokens.length > 0;
      // Regola: in automatico (al login) l'iscrizione si propone solo a chi non ha nessun token,
      // con la domanda. Chi ne ha già uno si registra a mano da Info utente (force).
      if (!force && automaticRegistration({ backendHasAnyToken }) === "skip") {
        return { skipped: true };
      }
      const needsPrompt = askConfirmation && !force && !backendHasAnyToken;

      if (needsPrompt) {
        if (pushPromptSkipRef.current) {
          return { skipped: true };
        }
        const accepted = await showQuestion({
          title: "Notifiche",
          message: "Vuoi registrare questo dispositivo per ricevere le notifiche EasyJob?",
          confirmLabel: "Sì, registra",
          cancelLabel: "Non ora",
        });
        if (!accepted) {
          pushPromptSkipRef.current = true;
          tracePushRefusal(activeUser);
          return { cancelled: true };
        }
      }

      const mustRegister = force || !backendHasThisDevice;
      if (!mustRegister) {
        return { alreadyRegistered: true };
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        await requirePermission();
        try {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: VAPID_PUBLIC_KEY,
          });
        } catch (err) {
          throw markTraced(
            new Error(tracePushError(activeUser, "subscribe_failed", { passo: "push_subscribe", modalita, error: err }))
          );
        }
      }

      const serialized = JSON.stringify(subscription);

      if (!activeUser.token) {
        throw markTraced(new Error(tracePushError(activeUser, "no_session_token", { passo: "session_check", modalita })));
      }

      try {
        try {
          await api.post("/api/push/subscribe_ws", {
            user_id: activeUser.id,
            subscription,
            session_token: activeUser.token,
          });
        } catch (err) {
          throw markTraced(
            new Error(tracePushError(activeUser, "backend_failed", { passo: "backend_subscribe", modalita, error: err }))
          );
        }

        const mergeTokens = (baseTokens) => {
          if (baseTokens.includes(serialized)) return baseTokens;
          return [...baseTokens, serialized];
        };

        setUser((prev) => {
          if (!prev) return prev;
          if (prev.id !== activeUser.id) return prev;
          const prevTokens = prev.token_notifiche
            ? prev.token_notifiche.split(";").filter(Boolean)
            : [];
          const merged = mergeTokens(prevTokens).join(";");
          const updated = {
            ...prev,
            token_notifiche: merged,
            token_notifiche_reg: true,
            token_notifiche_has_any: true,
            token_notifiche_local: serialized,
          };
          persistUserData(updated);
          return updated;
        });

        markPushRegisteredHere(activeUser.id);
        tracePushConfirm(activeUser, { modalita });

        if (needsPrompt) {
          await showMessage({
            title: "Notifiche",
            message: PUSH_MESSAGES.ok,
            type: "success",
            timeout: 3,
          });
        }

        return {
          success: true,
          message: PUSH_MESSAGES.ok,
        };
      } catch (err) {
        if (needsPrompt) {
          await showMessage({
            title: "Notifiche",
            message: err?.message || "Errore durante la registrazione del dispositivo.",
            type: "error",
          });
        }
        throw err;
      }
    },
    [user, showQuestion, showMessage, persistUserData]
  );

  useEffect(() => {
    if (!user?.id || !user?.token) {
      return;
    }

    let disposed = false;

    const checkPushStatus = async () => {
      try {
        let subscription = null;
        let serialized = null;
        if ("serviceWorker" in navigator && "PushManager" in window) {
          try {
            const registration = await navigator.serviceWorker.ready;
            subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              serialized = JSON.stringify(subscription);
            }
          } catch (err) {
            console.warn("Impossibile ottenere la subscription push", err);
          }
        }

        let data;
        try {
          data = await api.post("/api/push/check_token_notifiche", {
            user_id: user.id,
            session_token: user.token,
            ...(subscription ? { subscription } : {}),
          });
        } catch (err) {
          if (err instanceof ApiError) {
            throw new Error(err.body?.message || err.message || "Errore verifica token notifiche");
          }
          throw err;
        }
        if (data?.success === false) {
          throw new Error(data?.message || "Errore verifica token notifiche");
        }

        if (disposed) return;

        let nextMatch = Boolean(data.tokenMatch);
        let nextHasAny = Boolean(data.hasAnyToken);
        let subscriptionToken = serialized;

        // Il token di questo browser è già nella lista dell'utente: si è iscritto da qui (vale anche per
        // chi si era iscritto prima che esistesse la nota).
        if (nextMatch) {
          markPushRegisteredHere(user.id);
        }

        // Rinnovo silenzioso del token (scaduto o non più su Cedolino): solo se il permesso è già concesso
        // e l'utente si era già iscritto da questo dispositivo. Mai una prima iscrizione in silenzio.
        const renewal = silentRenewal({
          permission: typeof Notification !== "undefined" ? Notification.permission : "non_disponibile",
          registeredHere: wasPushRegisteredHere(user.id),
          hasSubscription: Boolean(subscription),
          tokenMatch: nextMatch,
        });
        if (renewal !== "none") {
          let activeSubscription = subscription;

          // Caso 1: il browser ha perso l'iscrizione — se ne crea una nuova
          if (renewal === "resubscribe") {
            try {
              const reg = await navigator.serviceWorker.ready;
              activeSubscription = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: VAPID_PUBLIC_KEY,
              });
              subscriptionToken = JSON.stringify(activeSubscription);
              console.log('[Push] Subscription rinnovata silenziosamente');
            } catch (err) {
              console.warn('[Push] Impossibile rinnovare la subscription:', err);
              tracePushError(user, "subscribe_failed", { passo: "rinnovo_silenzioso", modalita: "silenziosa", error: err });
            }
          }

          // Caso 2: il token non risulta su Cedolino — si registra di nuovo
          if (activeSubscription) {
            try {
              await api.post('/api/push/subscribe_ws', {
                user_id: user.id,
                subscription: activeSubscription,
                session_token: user.token,
              });
              nextMatch = true;
              nextHasAny = true;
              console.log('[Push] Token re-registrato silenziosamente su Cedolino');
              tracePushConfirm(user, { modalita: "silenziosa" });
            } catch (err) {
              console.warn('[Push] Impossibile re-registrare il token:', err);
              tracePushError(user, "backend_failed", { passo: "rinnovo_silenzioso", modalita: "silenziosa", error: err });
            }
          }
        }

        setUser((prev) => {
          if (!prev || prev.id !== user.id) return prev;
          const alreadyMatches =
            prev.token_notifiche_reg === nextMatch &&
            prev.token_notifiche_has_any === nextHasAny &&
            (!subscriptionToken || prev.token_notifiche_local === subscriptionToken);
          if (alreadyMatches) {
            return prev;
          }
          const updated = {
            ...prev,
            token_notifiche_reg: nextMatch,
            token_notifiche_has_any: nextHasAny,
            ...(subscriptionToken ? { token_notifiche_local: subscriptionToken } : {}),
          };
          persistUserData(updated);
          return updated;
        });
      } catch (err) {
        console.warn("Verifica token notifiche non riuscita", err);
      } finally {
        if (!disposed) {
          setPushStatusReady(true);
        }
      }
    };

    checkPushStatus();

    return () => {
      disposed = true;
    };
  }, [user?.id, user?.token, persistUserData, pushCheckTrigger]);

  // Ascolta messaggi dal Service Worker — PUSH_SUBSCRIPTION_CHECK triggera una verifica/rinnovo
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleSwMessage = (event) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHECK') {
        setPushCheckTrigger(t => t + 1);
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSwMessage);
  }, []);

  useEffect(() => {
    if (!user || !pushStatusReady) return;
    ensurePushSubscription({ targetUser: user, askConfirmation: true }).catch((err) => {
      tracePushUnexpected(user, err, "automatica");
      console.warn("Registrazione push automatica fallita", err);
    });
  }, [user, ensurePushSubscription, pushStatusReady]);

  useEffect(() => {
    if (!user) return;
    const handleOnlinePush = () => {
      if (!user) return;
      ensurePushSubscription({ targetUser: user, askConfirmation: true }).catch((err) => {
        tracePushUnexpected(user, err, "automatica");
        console.warn("Registrazione push post reconnect fallita", err);
      });
    };

    window.addEventListener("online", handleOnlinePush);
    return () => window.removeEventListener("online", handleOnlinePush);
  }, [ensurePushSubscription, user]);

  useEffect(() => {
    pushPromptSkipRef.current = false;
    setPushStatusReady(false);
  }, [user?.id]);

  const forcePushRegistration = useCallback(
    () =>
      ensurePushSubscription({ force: true }).catch((err) => {
        const message = tracePushUnexpected(user, err, "manuale");
        throw message ? new Error(message) : err;
      }),
    [ensurePushSubscription, user]
  );

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  window.setSection = setSection;

  return (
    <BrowserRouter>
      <div className="app-shell w-full flex flex-col items-center" style={{ height: "100dvh", overflow: "hidden" }}>
        <TopBar title={titles[section]} sectionIcon={sectionIcons[section]} onMenuClick={() => setMenuOpen(true)} isOnline={isOnline} actionButton={topBarAction} />
        {menuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-10" onClick={() => setMenuOpen(false)}>
            <div className="absolute left-0 top-0 w-64 h-full shadow-lg z-20 flex flex-col drawer-panel" onClick={e => e.stopPropagation()}>
              {user && (
                <div className="app-bar flex items-center gap-3 px-4 text-base font-semibold h-14">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    className="focus:outline-none flex-shrink-0"
                    title="Chiudi menu"
                  >
                    <span className="sr-only">Chiudi menu</span>
                    <div className="w-12 h-12 bg-white rounded-full overflow-hidden flex items-center justify-center">
                      <img src={EasyJobLogo} alt="EasyJob" className="h-10 w-auto" />
                    </div>
                  </button>
                  <span className="font-bold" style={{ fontSize: "1.7rem" }}>EasyJob</span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col divide-y divide-gray-100">
                  {canSeeTimbratore && (
                    <button
                      className={drawerButtonClass}
                      onClick={() => {
                        setSection("timbratore");
                        setMenuOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-3">
                        {drawerIcons.timbratore}
                        <span>Timbratore</span>
                      </span>
                    </button>
                  )}
                  {canSeeRichieste && (
                    <button
                      className={drawerButtonClass}
                      onClick={() => {
                        setSection("richieste");
                        setMenuOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-3">
                        {drawerIcons.storico}
                        <span>Richieste</span>
                      </span>
                    </button>
                  )}
                  {canSeeTurni && (
                    <button
                      className={drawerButtonClass}
                      onClick={() => {
                        setSection("turni");
                        setMenuOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-3">
                        {drawerIcons.turni}
                        <span>Turni</span>
                      </span>
                    </button>
                  )}
                  {canSeeMessaggi && (
                    <button
                      className={drawerButtonClass}
                      onClick={() => {
                        setSection("messaggi");
                        setMenuOpen(false);
                      }}
                    >
                      <span className="flex items-center gap-3">
                        {drawerIcons.messaggi}
                        <span>Messaggi</span>
                      </span>
                    </button>
                  )}
                  <button
                    className={drawerButtonClass}
                    onClick={() => {
                      setSection("infoUtente");
                      setMenuOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-3">
                      {drawerIcons.infoUtente}
                      <span>Info Utente</span>
                    </span>
                  </button>
                </div>
              </div>
              <div className="flex flex-col border-t border-gray-100 divide-y divide-gray-100">
                {canUseDevTools && (
                  <button
                    className={drawerButtonClass}
                    onClick={() => {
                      setSection("sviluppo");
                      setMenuOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-3">
                      {drawerIcons.sviluppo}
                      <span>Sviluppo</span>
                    </span>
                  </button>
                )}
                <button
                  className={`${drawerButtonClass} text-red-500`}
                  onClick={handleLogout}
                >
                  <span className="flex items-center gap-3">
                    {drawerIcons.logout}
                    <span className="truncate">{user?.username}</span>
                  </span>
                </button>
                <div className="px-4 py-3 text-sm font-semibold text-gray-700 flex items-end justify-between gap-4">
                  <div className="flex flex-col">
                    <span>Tema</span>
                    <span className="text-xs font-normal text-gray-400">Dark / Light</span>
                  </div>
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className={`self-end w-14 h-8 rounded-full border transition ${
                      theme === "dark" ? "bg-gray-900 border-gray-900" : "bg-blue-100 border-blue-200"
                    } relative`}
                    title="Cambia tema"
                  >
                    <span className="sr-only">Cambia tema</span>
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
                        theme === "dark" ? "translate-x-6" : "translate-x-0"
                      }`}
                    >
                      {theme === "dark" ? "🌙" : "☀️"}
                    </span>
                  </button>
                </div>
                <div className="px-4 py-3 text-xs text-gray-400">
                  <div className="flex items-center gap-3 justify-center">
                    <img src={LogoUssBlu} alt="USS" className="h-7 w-auto" />
                    <div className="text-center">
                      <div>
                        Versione {APP_VERSION}{process.env.REACT_APP_EASYJOB_ENV === 'dev' && ' - DEV'}
                      </div>
                      <a
                        href="https://www.ussolutions.eu/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline hover:text-blue-700 transition"
                        title="Visita il sito USS"
                      >
                        ©2026 - USS<br />All rights reserved.
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="w-full flex-1">
          {section === "richieste" && canSeeRichieste && (
            <RichiesteStorico
              user={user}
              setUser={setUser}
              isOnline={isOnline}
              onSetTopBarAction={setTopBarAction}
            />
          )}
          {section === "infoUtente" && (
            <InfoUtente
              user={user}
              setUser={setUser}
              isOnline={isOnline}
              onForcePushRegistration={forcePushRegistration}
              onSetTopBarAction={setTopBarAction}
            />
          )}
          {section === "turni" && canSeeTurni && <Turni user={user} isOnline={isOnline} onSetTopBarAction={setTopBarAction} />}
          {section === "timbratore" && <Timbratore user={user} onSetTopBarAction={setTopBarAction} />}
          {section === "messaggi" && canSeeMessaggi && <MessaggiStorico user={user} isOnline={isOnline} onSetTopBarAction={setTopBarAction} />}
          {section === "sviluppo" && canUseDevTools && <DevTools onSetTopBarAction={setTopBarAction} />}
        </div>
        {/* <button
          className="p-4 text-left bg-blue-100 hover:bg-blue-200 rounded mt-2"
          onClick={async () => {
            // Chiamata manuale al servizio di invio notifiche pendenti
            await fetch(`${API_BASE_URL}/api/push/send-pending`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            });
            alert('Servizio invio notifiche pendenti avviato!');
          }}
        >
          Test Invio Notifiche Pendenti
        </button> 
        <button onClick={testInvioNotifiche}>Test Invio Notifiche Pendenti</button> */}
      </div>
    </BrowserRouter>
  );
}

export default App;